import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Modal } from '@cuito/ui';
import { stockApi, type WarehouseView } from '../api/stock';
import { readServerMessage } from '../lib/apiError';
import { useAuthStore } from '../stores/authStore';

interface WarehouseFormState {
  name: string;
  address: string;
  note: string;
}

const EMPTY_FORM: WarehouseFormState = { name: '', address: '', note: '' };

/**
 * 仓库管理（`05-ui-design-system.md` §3.2 的仓库页）。
 *
 * 一个社团可以有**多个**仓库：列表直接取 `GET /warehouses`，其中
 * `itemKindCount` / `totalAvailableQty` 是服务端算好的（可用量 > 0 的种类与总量），
 * 前端不再拉一遍库存项自己数——两处各算一次迟早对不上。
 *
 * **删除能不能做由服务端说了算**：本页不做「有库存就不给删」的本地判断，
 * 直接把 `WAREHOUSE_NOT_EMPTY` 的原文提示出来（含剩余种类数）。
 */
export default function WarehousesPage() {
  const canWrite = useAuthStore((s) => s.hasPermission('warehouse:write'));

  const [warehouses, setWarehouses] = useState<WarehouseView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<WarehouseFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setWarehouses(await stockApi.listWarehouses());
    } catch {
      setError('加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (): Promise<void> => {
    if (form.name.trim() === '') {
      setFormError('请填写仓库名称');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      await stockApi.createWarehouse({
        name: form.name.trim(),
        ...(form.address.trim() === '' ? {} : { address: form.address.trim() }),
        ...(form.note.trim() === '' ? {} : { note: form.note.trim() }),
      });
      setShowForm(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (e) {
      setFormError(readServerMessage(e) ?? '保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (warehouse: WarehouseView): Promise<void> => {
    if (!window.confirm(`确定删除仓库「${warehouse.name}」吗？该仓库下的库存记录将被移除。`)) {
      return;
    }
    try {
      await stockApi.deleteWarehouse(warehouse.id);
      await load();
    } catch (e) {
      // 有库存时后端回 400 WAREHOUSE_NOT_EMPTY，其 message 带剩余种类数，原样展示
      setError(readServerMessage(e) ?? '删除失败，请重试');
    }
  };

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">仓库</h1>

      <div className="mb-4 flex items-center justify-end">
        {canWrite && (
          <Button
            onClick={() => {
              setForm(EMPTY_FORM);
              setFormError(null);
              setShowForm(true);
            }}
          >
            + 新建仓库
          </Button>
        )}
      </div>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error && <p className="font-bold text-red-600">{error}</p>}

      {!loading && !error && warehouses.length === 0 && (
        <p className="text-gray-500">还没有仓库，点击「新建仓库」创建</p>
      )}

      {!loading && !error && warehouses.length > 0 && (
        <table className="w-full rounded-lg border-2 border-black">
          <thead className="bg-gray-50">
            <tr>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">名称</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">地址</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">库存种类</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">可用合计</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">地点码</th>
              {canWrite && (
                <th className="border-b-2 border-black px-4 py-3 text-left font-bold">操作</th>
              )}
            </tr>
          </thead>
          <tbody>
            {warehouses.map((warehouse) => (
              <tr key={warehouse.id}>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{warehouse.name}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {warehouse.address ?? '-'}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {warehouse.itemKindCount} 种
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {warehouse.totalAvailableQty} 件
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {warehouse.location?.code ?? '-'}
                </td>
                {canWrite && (
                  <td className="border-b border-gray-200 px-4 py-3 text-sm">
                    <button
                      onClick={() => void remove(warehouse)}
                      className="font-medium text-red-600 hover:text-red-800"
                    >
                      删除
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal open={showForm} onClose={() => setShowForm(false)} title="新建仓库" maxWidth="max-w-md">
        <div className="flex flex-col gap-3">
          {formError && (
            <p className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600">
              {formError}
            </p>
          )}

          <Input
            label="仓库名称"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="地址"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
          <Input
            label="备注"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />

          <div className="mt-2 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>
              取消
            </Button>
            <Button onClick={() => void create()} disabled={saving} className="disabled:opacity-50">
              {saving ? '创建中…' : '确认'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
